import { useDroppable } from "@dnd-kit/core";
import type { IChatFolderListObject } from "@frontend-shared/types";
import { useDirection } from "@radix-ui/react-direction";
import { ChevronDown, Folder, FolderOpen } from "lucide-react";
import React, { memo, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";

import { useChatList } from "@/api/chat/useChatList";
import { useDeleteChatFolder } from "@/api/chat-folder/useDeleteChatFolder";
import { useRenameChatFolder } from "@/api/chat-folder/useRenameChatFolder";
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { PATHS } from "@/constants/paths";
import { cn } from "@/lib/utils";

import { ChatListItem } from "../../ChatList/components/ChatListItem";
import { useSidebarInfiniteScroll } from "../../hooks/use-sidebar-infinite-scroll";
import { ChatFolderActionMenu } from "./ChatFolderActionMenu";
import { DeleteChatFolderDialog } from "./DeleteChatFolderDialog";
import { FolderSettingsDialog } from "./FolderSettingsDialog";
import { RenameChatFolderDialog } from "./RenameChatFolderDialog";

interface IChatFolderListItemProps {
  folder: IChatFolderListObject;
  activeChatFolderId?: string | null;
}

const ChatFolderListItemInner: React.FC<IChatFolderListItemProps> = ({
  folder,
  activeChatFolderId,
}) => {
  const { chatId } = useParams<{ chatId: string }>();
  const navigate = useNavigate();
  const dir = useDirection();

  const { setNodeRef, isOver } = useDroppable({
    id: folder.uuid,
    data: { type: "folder", folderUuid: folder.uuid },
  });
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isRenameDialogOpen, setIsRenameDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [isSettingsDialogOpen, setIsSettingsDialogOpen] = useState(false);
  const [hasBeenOpened, setHasBeenOpened] = useState(false);

  const { mutateAsync: renameFolder } = useRenameChatFolder();
  const { mutateAsync: deleteFolder } = useDeleteChatFolder();

  const { ref: infiniteScrollRef, isInView } = useSidebarInfiniteScroll();

  // NOTE: Chats inside this folder are fetched lazily when the accordion is expanded
  const {
    data: chatData,
    isLoading: isChatsLoading,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useChatList({ folderId: folder.uuid, enabled: hasBeenOpened });
  const folderChats = useMemo(
    () => chatData?.pages.flatMap((page) => page.data) ?? [],
    [chatData?.pages],
  );

  // NOTE: Infinite scroll for folder chats
  useEffect(() => {
    if (isInView && hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  }, [isInView, hasNextPage, isFetchingNextPage, fetchNextPage]);

  const handleRename = useCallback(
    async (name: string): Promise<void> => {
      await renameFolder({ uuid: folder.uuid, name });
    },
    [renameFolder, folder.uuid],
  );

  const handleDelete = useCallback(
    async (moveChatsToRoot: boolean): Promise<void> => {
      // NOTE: Redirect if the active chat belongs to this folder, even when
      // the folder accordion stayed closed after a refresh or direct URL load.
      const isCurrentChatInFolder =
        chatId !== undefined &&
        (activeChatFolderId === folder.uuid ||
          folderChats.some((chat) => chat.uuid === chatId));

      await deleteFolder({ uuid: folder.uuid, moveChatsToRoot });

      if (!moveChatsToRoot && isCurrentChatInFolder) {
        navigate(PATHS.CHAT);
      }
    },
    [
      activeChatFolderId,
      deleteFolder,
      folder.uuid,
      chatId,
      folderChats,
      navigate,
    ],
  );

  const handleNewChat = useCallback((): void => {
    navigate(`${PATHS.CHAT}?folderId=${folder.uuid}`);
  }, [navigate, folder.uuid]);

  const handleSaveInstructions = useCallback(
    async (customInstructions: string): Promise<void> => {
      await renameFolder({
        uuid: folder.uuid,
        name: folder.name,
        customInstructions,
      });
    },
    [renameFolder, folder.uuid, folder.name],
  );

  const isRtl = dir === "rtl";

  return (
    <>
      <AccordionItem
        ref={setNodeRef}
        value={folder.uuid}
        className={cn(
          "border-b-0 w-full min-w-0 rounded-md transition-colors",
          isOver && "ring-2 ring-primary bg-accent",
        )}
        data-folder-id={folder.uuid}
      >
        <div className="group/folder-item relative max-w-full rounded-xl hover:bg-sidebar-accent transition-colors [--menu-space:1rem] hover:[--menu-space:2.5rem]">
          <AccordionTrigger
            className="w-full h-8 items-center gap-2 rounded-xl px-3 text-sm hover:no-underline transition-colors"
            onClick={() => setHasBeenOpened(true)}
            folderIcons={{ closed: Folder, open: FolderOpen }}
            chevronElement={
              <span className="absolute top-1/2 -translate-y-1/2 ltr:right-0 rtl:left-0 h-6 w-7 flex items-center justify-center">
                <ChevronDown
                  className={cn(
                    "h-4 w-4 shrink-0 transition-all duration-200 group-data-[state=open]/accordion:rotate-180 group-hover/folder-item:opacity-0",
                    isMenuOpen && "opacity-0",
                  )}
                />
              </span>
            }
          >
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span
                    className={cn(
                      "flex-1 text-start overflow-hidden whitespace-nowrap",
                      isRtl ? "chat-mask-fade-left" : "chat-mask-fade-right",
                      isMenuOpen && (isRtl ? "pl-8" : "pr-8"),
                    )}
                  >
                    {folder.name}
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{folder.name}</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </AccordionTrigger>
          <ChatFolderActionMenu
            isMenuOpen={isMenuOpen}
            onOpenChange={setIsMenuOpen}
            onRenameClick={() => {
              setIsRenameDialogOpen(true);
              setIsMenuOpen(false);
            }}
            onDeleteClick={() => {
              setIsDeleteDialogOpen(true);
              setIsMenuOpen(false);
            }}
            onNewChatClick={() => {
              setIsMenuOpen(false);
              handleNewChat();
            }}
            onSettingsClick={() => {
              setIsSettingsDialogOpen(true);
              setIsMenuOpen(false);
            }}
          />
        </div>
        <AccordionContent className="w-full min-w-0 pt-0.5 pb-1 pl-2 space-y-0.5">
          {isChatsLoading ? (
            <div className="flex justify-center py-2">
              <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <>
              {folderChats.map((chat) => (
                <ChatListItem key={chat.uuid} chat={chat} />
              ))}
              <div ref={infiniteScrollRef}>
                {isFetchingNextPage && (
                  <div className="flex justify-center py-2">
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
                  </div>
                )}
              </div>
            </>
          )}
        </AccordionContent>
      </AccordionItem>

      <RenameChatFolderDialog
        folder={folder}
        isOpen={isRenameDialogOpen}
        onOpenChange={setIsRenameDialogOpen}
        onRename={handleRename}
      />
      <DeleteChatFolderDialog
        folder={folder}
        isOpen={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onDelete={handleDelete}
      />
      <FolderSettingsDialog
        folder={folder}
        isOpen={isSettingsDialogOpen}
        onOpenChange={setIsSettingsDialogOpen}
        onSave={handleSaveInstructions}
      />
    </>
  );
};

export const ChatFolderListItem = memo(ChatFolderListItemInner);
